import { useEffect, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import InputGroup from '../InputGroup/InputGroup';
import Modal from '../Modal/Modal';

const MainFieldConfigModal = ({ isOpen, onClose, field, onSave, showToast }) => {
  const [config, setConfig] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (field && isOpen) {
      setConfig(field.config || {});
    }
  }, [field, isOpen]);

  if (!field) return null;

  const handleConfigChange = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updatedField = { ...field, config: config };
      await onSave(updatedField);
      onClose();
    } catch (error) {
      console.error('Failed to save field config:', error);
      showToast?.('error', __('Error', 'havenlytics'), __('Failed to save field configuration', 'havenlytics'));
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Get the taxonomy edit link URL
   */
  const getTaxonomyEditLink = () => {
    let taxonomy = '';
    if (field.id === 'property_type') {
      taxonomy = 'hvnly_prop_types';
    } else if (field.id === 'location') {
      taxonomy = 'hvnly_prop_locations';
    } else if (field.config?.taxonomy) {
      taxonomy = field.config.taxonomy;
    } else {
      taxonomy = 'hvnly_prop_types';
    }
    
    return `/wp-admin/edit-tags.php?taxonomy=${taxonomy}&post_type=hvnly_property`;
  };

  /**
   * Get taxonomy label for display
   */
  const getTaxonomyLabel = () => {
    if (field.id === 'property_type') {
      return __('Property Types', 'havenlytics');
    } else if (field.id === 'location') {
      return __('Locations', 'havenlytics');
    } else if (field.config?.taxonomy) {
      const labels = {
        'hvnly_prop_types': __('Property Types', 'havenlytics'),
        'hvnly_prop_locations': __('Locations', 'havenlytics'),
        'hvnly_prop_features': __('Features', 'havenlytics'),
        'hvnly_prop_tags': __('Tags', 'havenlytics'),
        'hvnly_prop_badges': __('Badges', 'havenlytics'),
        'hvnly_prop_depts': __('Status/Department', 'havenlytics'),
      };
      return labels[field.config.taxonomy] || __('Taxonomy Terms', 'havenlytics');
    }
    return __('Taxonomy Terms', 'havenlytics');
  };

  const renderConfigContent = () => {
    if (field.type === 'text') {
      return (
        <div className="hvnly-main-field-config">
          <div className="hvnly-main-field-config__section">
            <h4>{__('Text Field Settings', 'havenlytics')}</h4>
            <InputGroup
              label={__('Placeholder', 'havenlytics')}
              id="placeholder"
              value={config.placeholder || __('Search Address', 'havenlytics')}
              onChange={(value) => handleConfigChange('placeholder', value)}
            />
          </div>
        </div>
      );
    }
    
    if (field.type === 'taxonomy') {
      return (
        <div className="hvnly-main-field-config">
          <div className="hvnly-main-field-config__section">
            <h4>{__('Taxonomy Field Settings', 'havenlytics')}</h4>
            
            <InputGroup
              label={__('Placeholder', 'havenlytics')}
              id="placeholder"
              value={config.placeholder || (field.id === 'property_type' ? __('Property Type', 'havenlytics') : __('Search Location', 'havenlytics'))}
              onChange={(value) => handleConfigChange('placeholder', value)}
            />
            
            <div className="hvnly-main-field-config__info">
              <i className="fas fa-info-circle"></i>
              <div>
                <p>
                  <strong>{getTaxonomyLabel()}</strong>
                </p>
                <p>
                  {__('This field uses taxonomy terms. Terms are managed in WordPress admin under:', 'havenlytics')}
                </p>
                <p style={{ marginTop: '8px' }}>
                  <code>
                    {__('Properties → Property Taxonomies', 'havenlytics')}
                  </code>
                </p>
                <p style={{ marginTop: '12px' }}>
                  <a 
                    href={getTaxonomyEditLink()} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '6px',
                      color: 'var(--hvnly-brand-primary)',
                      textDecoration: 'none'
                    }}
                  >
                    <i className="fas fa-external-link-alt"></i>
                    {__('Manage taxonomy terms', 'havenlytics')}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="hvnly-main-field-config__info">
        <i className="fas fa-info-circle"></i>
        <p>{__('This field type does not require additional configuration.', 'havenlytics')}</p>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleSave}
      title={`${__('Configure', 'havenlytics')}: ${field.title}`}
      size="large"
      confirmText={__('Save Configuration', 'havenlytics')}
      isLoading={isSaving}
    >
      <div className="hvnly-main-field-config-modal-content">
        {renderConfigContent()}
      </div>
    </Modal>
  );
};

export default MainFieldConfigModal;